import type {
  ApiActivity,
  ApiWalletTransactionCategory,
  ApiWalletTransactionCounterparty,
} from '../api/types';
import type { LangFn } from './langProvider';

export const WALLET_SYSTEM_COUNTERPARTY_ID = 'system-legends-energy';
export const WALLET_SYSTEM_COUNTERPARTY_NAME = 'Legends Energy';
export const WALLET_SYSTEM_CATEGORY_IDS = {
  feeBalanceTopup: 'system-fee-balance-topup',
} as const;

export type WalletSystemMetadataKind = keyof typeof WALLET_SYSTEM_CATEGORY_IDS;

export type WalletSystemMetadata = {
  counterparty: ApiWalletTransactionCounterparty;
  category: ApiWalletTransactionCategory;
};

const SYSTEM_OWNER = 'system';
const SYSTEM_UPDATED_AT = '1970-01-01T00:00:00.000Z';

const CATEGORY_PRESENTATION: Record<WalletSystemMetadataKind, {
  name: string;
  emoji: string;
  color: string;
}> = {
  feeBalanceTopup: {
    name: '$prepaid_history_topup',
    emoji: '💳',
    color: '#CCE9E5',
  },
};

function getWalletSystemCounterparty(): ApiWalletTransactionCounterparty {
  return {
    id: WALLET_SYSTEM_COUNTERPARTY_ID,
    name: WALLET_SYSTEM_COUNTERPARTY_NAME,
    normalized_name: 'legends energy',
    owner_address: SYSTEM_OWNER,
    updated_at: SYSTEM_UPDATED_AT,
  };
}

/** System metadata is generated for presentation and never enters the encrypted user directory. */
export function getWalletSystemMetadata(kind: WalletSystemMetadataKind, lang: LangFn): WalletSystemMetadata {
  const presentation = CATEGORY_PRESENTATION[kind];

  return {
    counterparty: getWalletSystemCounterparty(),
    category: {
      id: WALLET_SYSTEM_CATEGORY_IDS[kind],
      name: lang(presentation.name),
      emoji: presentation.emoji,
      color: presentation.color,
      owner_address: SYSTEM_OWNER,
      updated_at: SYSTEM_UPDATED_AT,
    },
  };
}

export function getActivityWalletSystemMetadata(
  activity: ApiActivity,
  lang: LangFn,
): Partial<WalletSystemMetadata> | undefined {
  if (activity.kind !== 'transaction') return undefined;

  if (activity.extra?.walletPrepaidTopup) {
    return getWalletSystemMetadata('feeBalanceTopup', lang);
  }

  return undefined;
}

export function getPrepaidWalletSystemMetadata(type: string, lang: LangFn): Partial<WalletSystemMetadata> | undefined {
  if (type === 'topup' || type === 'topup_credit' || type === 'topup_resource_debit') {
    return getWalletSystemMetadata('feeBalanceTopup', lang);
  }

  if (type === 'sponsorship_debit') {
    return { counterparty: getWalletSystemCounterparty() };
  }

  return undefined;
}

export function isWalletSystemCategory(category: { id: string }) {
  return Object.values(WALLET_SYSTEM_CATEGORY_IDS).includes(
    category.id as (typeof WALLET_SYSTEM_CATEGORY_IDS)[WalletSystemMetadataKind],
  );
}

export function isWalletSystemCounterparty(counterparty: { id: string }) {
  return counterparty.id === WALLET_SYSTEM_COUNTERPARTY_ID;
}

export function isWalletSystemMetadataRecord(record: { id: string }) {
  return isWalletSystemCounterparty(record) || isWalletSystemCategory(record);
}
